import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AddTeacherStandaloneMutationVariables,
  useAddTeacherStandaloneMutation,
} from 'core';

import {
  ToastTypes,
  GlobalToastTypeNames,
  ErrorsType,
  FieldErrorTypes,
} from 'types';
import { ITeacherAddFieldsValues } from 'molecules';

import { useToasts } from '../Toast/useToasts';

type ICreateTeacherHookType = [
  (
    variables: AddTeacherStandaloneMutationVariables,
    existingUser?: boolean,
  ) => void,
  {
    loading: boolean;
    errors: ErrorsType<ITeacherAddFieldsValues> | null;
  },
];

// Server form errors key fields in snake_case (`first_name`); the form values
// are camelCase (`firstName`). Map so a server-side field error highlights the
// right input rather than silently falling through to the generic toast.
const SERVER_FIELD_MAP: Record<string, keyof ITeacherAddFieldsValues> = {
  first_name: 'firstName',
  last_name: 'lastName',
  middle_name: 'middleName',
  email: 'email',
  phone: 'phone',
};

/**
 * Standalone («свободный») teacher creation (spec 0007 rev.). Wraps the
 * `createTeacher` mutation — no application / employment-type steps — with
 * success/error toasts. On success the new teacher's id is handed to
 * `onSuccess` so the caller can navigate to the teacher card. Server field
 * errors are surfaced back to the form.
 *
 * No `refetchQueries`: on success we navigate to the new teacher's card and the
 * teachers list refetches on remount. An unfiltered refetch here would clobber
 * the shared (keyArgs-less) `allTeachers` cache bucket and silently revert an
 * active table filter (and, on a failed create, leave the list unfiltered while
 * the filter dropdowns still read as selected).
 */
export const useCreateTeacher = (
  onSuccess?: (teacherId: string) => void,
): ICreateTeacherHookType => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [
    errors,
    setErrors,
  ] = useState<ErrorsType<ITeacherAddFieldsValues> | null>(null);

  const [
    addTeacher,
    { data, loading, error },
  ] = useAddTeacherStandaloneMutation();

  // Whether the last submit added the teacher role to an already-existing
  // person (vs created a brand-new user) — chooses the success message. A ref,
  // not state, so it's read fresh in the success effect without an extra render.
  const roleAddedRef = useRef(false);

  const createTeacher = useCallback(
    (
      { teacher }: AddTeacherStandaloneMutationVariables,
      existingUser = false,
    ) => {
      roleAddedRef.current = existingUser;
      setErrors(null);
      addTeacher({ variables: { teacher } });
    },
    [addTeacher],
  );

  // Transport-level failure (network error, or an unexpected server exception
  // that never reached the mutation payload) — surface it instead of letting
  // the button stop spinning with no feedback at all.
  useEffect(() => {
    if (!error) return;
    addToast({
      data: {
        type: ToastTypes.error,
        title: `${t('common.somethingWentWrong')}`,
        text: error.message,
      },
      type: GlobalToastTypeNames.TeacherAdd,
    });
  }, [error, addToast, t]);

  useEffect(() => {
    const payload = data?.createTeacher;
    if (!payload) return;

    if (payload.success && payload.teacher?.id) {
      addToast({
        data: {
          type: ToastTypes.success,
          title: roleAddedRef.current
            ? `${t('teacherPopup.addRoleSuccess')}`
            : `${t('teacherPopup.createSuccess')}`,
        },
        type: GlobalToastTypeNames.TeacherAdd,
      });
      if (onSuccess) onSuccess(payload.teacher.id);
      return;
    }

    const serverErrors = payload.customErrors;
    if (serverErrors && serverErrors.length) {
      const fieldErrors: ErrorsType<ITeacherAddFieldsValues> = {};
      serverErrors.forEach((serverError) => {
        if (serverError?.field) {
          const key =
            SERVER_FIELD_MAP[serverError.field] ||
            (serverError.field as keyof ITeacherAddFieldsValues);
          fieldErrors[key] = {
            type: FieldErrorTypes.SERVER_ERROR,
            fieldName: key,
            message: serverError.message || '',
          };
        }
      });
      setErrors(Object.keys(fieldErrors).length ? fieldErrors : null);

      addToast({
        data: {
          type: ToastTypes.error,
          title: `${t('common.somethingWentWrong')}`,
          text: serverErrors
            .map((serverError) => serverError?.message)
            .filter(Boolean)
            .join('\n'),
        },
        type: GlobalToastTypeNames.TeacherAdd,
      });
    }
  }, [data, addToast, t, onSuccess]);

  return [
    createTeacher,
    {
      loading,
      errors,
    },
  ];
};
